import { CSSProperties, useState } from "react";
import { CircularProgress, Tooltip, Typography } from "@mui/material";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import ErrorIcon from "@mui/icons-material/Error";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Card, useBreakpoint } from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import { useCrmSyncState } from "@features/Contacts/hooks/useCrmSyncState";
import { useGoogleContactsAccess } from "@features/Contacts/hooks/useGoogleContactsAccess";
import { CrmSyncDetailsDialog } from "@features/Contacts/components/CrmSyncDetailsDialog";

const NORMAL_ICON_SIZE = 20;
const COMPACT_ICON_SIZE = 16;

const Wrapper = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem 1rem;
  color: ${(p) => p.theme.text.colors.primary};

  ${(p) => p.theme.media.mobileDown} {
    padding: 0.5rem 1rem;
    min-height: 3rem;
  }

  &[data-compact="true"] {
    padding: 0;
    min-height: 0;
  }
`;

const SyncIndicatorCard = styled(Card)`
  display: flex;
  align-items: center;

  &[data-compact="true"] {
    border: none;
    background-color: transparent;
    margin-left: auto;
  }
`;

const IconSlot = styled(Box)`
  position: relative;
  width: ${NORMAL_ICON_SIZE}px;
  height: ${NORMAL_ICON_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &[data-compact="true"] {
    width: ${COMPACT_ICON_SIZE}px;
    height: ${COMPACT_ICON_SIZE}px;
  }
`;

const ErrorBadge = styled(Box)`
  position: absolute;
  bottom: -4px;
  right: -4px;
  line-height: 0;
  background-color: ${(p) => p.theme.colors.background};
  border-radius: 50%;
`;

type Props = {
  syncingLabel?: string;
  idleLabel?: string;
  errorLabel?: string;
  style?: CSSProperties;
  compact?: boolean;
};

export const CrmSyncIndicator = ({
  syncingLabel = "Syncing...",
  idleLabel = "Up to date",
  errorLabel = "Sync errors",
  style,
  compact = false,
}: Props) => {
  const { hasAccess } = useGoogleContactsAccess();
  const syncState = useCrmSyncState({ enabled: hasAccess });
  const [dialogOpen, setDialogOpen] = useState(false);
  const status = syncState.status;
  const isMobile = useBreakpoint("mobile");
  const hideLabel = compact && isMobile;
  const iconSize = compact ? COMPACT_ICON_SIZE : NORMAL_ICON_SIZE;
  const compactAttr = compact ? "true" : undefined;

  if (status === "disabled") {
    return null;
  }

  if (status === "loading") {
    return (
      <SyncIndicatorCard data-compact={compactAttr} style={style}>
        <Wrapper data-compact={compactAttr}>
          <IconSlot data-compact={compactAttr}>
            <CircularProgress size={iconSize} />
          </IconSlot>
          {!hideLabel && (
            <Typography variant="caption">{syncingLabel}</Typography>
          )}
        </Wrapper>
      </SyncIndicatorCard>
    );
  }

  if (status === "fetch-error") {
    return (
      <Tooltip title="Could not load sync status" placement="right">
        <SyncIndicatorCard data-compact={compactAttr} style={style}>
          <Wrapper data-compact={compactAttr}>
            <IconSlot data-compact={compactAttr}>
              <ErrorOutlineIcon sx={{ fontSize: iconSize }} />
            </IconSlot>
            {!hideLabel && (
              <Typography variant="caption">Status unavailable</Typography>
            )}
          </Wrapper>
        </SyncIndicatorCard>
      </Tooltip>
    );
  }

  const { hasErrors } = syncState;

  const label =
    status === "syncing" ? syncingLabel : hasErrors ? errorLabel : idleLabel;

  return (
    <>
      <SyncIndicatorCard
        data-compact={compactAttr}
        style={{ ...style, cursor: "pointer" }}
        onClick={() => setDialogOpen(true)}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-label="View CRM sync details"
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDialogOpen(true);
          }
        }}
      >
        <Wrapper data-compact={compactAttr}>
          <IconSlot data-compact={compactAttr}>
            {status === "syncing" ? (
              <CircularProgress size={iconSize} />
            ) : (
              <CloudDoneOutlinedIcon sx={{ fontSize: iconSize }} />
            )}
            {hasErrors && (
              <ErrorBadge>
                <ErrorIcon sx={{ fontSize: 13, color: "error.main" }} />
              </ErrorBadge>
            )}
          </IconSlot>
          {!hideLabel && <Typography variant="caption">{label}</Typography>}
        </Wrapper>
      </SyncIndicatorCard>

      <CrmSyncDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        syncState={syncState}
      />
    </>
  );
};
